import React from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'sonner';

import {
  Card, CardContent, makeStyles, Typography,
} from '@material-ui/core';
import AccountBalanceWalletRounded from '@material-ui/icons/AccountBalanceWalletRounded';
import CreditCardRounded from '@material-ui/icons/CreditCardRounded';
import ReceiptRounded from '@material-ui/icons/ReceiptRounded';
import WarningRounded from '@material-ui/icons/WarningRounded';

import CustomButton from 'creative-components/CustomButtons/Button';

import InvoicesTable from 'components/InvoicesTable/InvoicesTable';
import PaymentMethodSelector from 'components/PaymentMethods/PaymentMethodSelector';

import { getUserInvoices, updateCampaign } from 'utils/api';
import { updateAdCampaign } from 'utils/api/ad-campaigns';
import { formatCents } from 'utils/lib';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex', flexDirection: 'column', gap: theme.spacing(3), marginTop: theme.spacing(3),
  },
  billingLayout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(320px, 0.85fr)',
    gap: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  card: {
    height: 'auto',
    overflow: 'hidden',
    margin: '0 !important',
    padding: '0 !important',
    border: `1px solid ${theme.palette.grayScale6.main}`,
    borderRadius: 16,
    backgroundColor: theme.palette.white.main,
    boxShadow: '0 10px 30px rgba(23, 23, 24, 0.045)',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(2.25, 3),
    borderBottom: `1px solid ${theme.palette.grayScale6.main}`,
    background: 'linear-gradient(135deg, rgba(150, 214, 179, 0.20) 0%, rgba(246, 246, 247, 0.72) 100%)',
    [theme.breakpoints.down('xs')]: {
      alignItems: 'flex-start',
      padding: theme.spacing(2, 2.25),
    },
  },
  cardHeading: {
    display: 'flex', alignItems: 'center', gap: theme.spacing(1.5), minWidth: 0,
  },
  cardIcon: {
    width: 42,
    height: 42,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: theme.palette.primary.dark,
    backgroundColor: theme.palette.white.main,
    border: '1px solid rgba(86, 145, 127, 0.18)',
    borderRadius: 12,
    boxShadow: '0 4px 12px rgba(60, 85, 71, 0.08)',
    '& svg': { fontSize: 21 },
  },
  cardTitle: {
    color: theme.palette.darkGray.main,
    fontSize: 17,
    fontWeight: 700,
    lineHeight: 1.35,
  },
  cardDescription: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    lineHeight: 1.5,
    marginTop: 2,
  },
  cardBody: {
    padding: theme.spacing(3),
    '&:last-child': { paddingBottom: theme.spacing(3) },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2.25),
      '&:last-child': { paddingBottom: theme.spacing(2.25) },
    },
  },
  summaryGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: theme.spacing(1.5),
  },
  summaryItem: {
    minWidth: 0,
    padding: theme.spacing(1.75, 2),
    border: `1px solid ${theme.palette.grayScale6.main}`,
    borderRadius: 12,
    backgroundColor: theme.palette.grayScale8.main,
  },
  label: {
    color: theme.palette.text.secondary, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em',
  },
  value: {
    color: theme.palette.darkGray.main,
    fontSize: 15,
    fontWeight: 700,
    marginTop: 5,
  },
  summaryContent: {
    minWidth: 0,
    '& > div': {
      margin: '0 !important',
    },
  },
  overdue: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    color: theme.palette.error.dark,
    background: '#FFF7F6',
    padding: theme.spacing(1.5, 2),
    border: '1px solid #F6D5D1',
    borderRadius: 10,
    marginTop: theme.spacing(2),
    '& svg': {
      fontSize: 20,
      flexShrink: 0,
    },
  },
  methodControl: { maxWidth: 440 },
  paymentPanel: {
    minWidth: 0,
    paddingLeft: theme.spacing(3),
    borderLeft: `1px solid ${theme.palette.grayScale6.main}`,
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(3),
      paddingLeft: 0,
      borderTop: `1px solid ${theme.palette.grayScale6.main}`,
      borderLeft: 0,
    },
  },
  paymentTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    color: theme.palette.darkGray.main,
    fontSize: 15,
    fontWeight: 700,
    '& svg': {
      color: theme.palette.primary.dark,
      fontSize: 19,
    },
  },
  paymentHelper: { marginTop: theme.spacing(1.5) },
  billingButton: {
    flexShrink: 0,
    margin: '0 !important',
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      marginTop: `${theme.spacing(1.5)}px !important`,
    },
  },
}));

type BillingCardHeaderProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
  action?: React.ReactNode;
};

const BillingCardHeader: React.FC<BillingCardHeaderProps> = ({
  icon, title, description, action,
}) => {
  const classes = useStyles();
  return (
    <div className={classes.cardHeader}>
      <div className={classes.cardHeading}>
        <span className={classes.cardIcon} aria-hidden="true">{icon}</span>
        <div>
          <Typography component="h2" className={classes.cardTitle}>{title}</Typography>
          <Typography component="p" className={classes.cardDescription}>{description}</Typography>
        </div>
      </div>
      {action}
    </div>
  );
};

type Props = {
  type: 'campaign' | 'ad-campaign';
  id: string;
  paymentMethodId?: string;
  amount?: number;
  cadence: string;
  nextBillingDate?: string | Date;
  editable: boolean;
  onUpdated: () => Promise<any> | void;
  summaryContent?: React.ReactNode;
};

const ProductBillingTab: React.FC<Props> = ({
  type, id, paymentMethodId, amount, cadence, nextBillingDate, editable, onUpdated, summaryContent,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const [invoices, setInvoices] = React.useState<any[] | undefined>();
  const [selectedMethodId, setSelectedMethodId] = React.useState(paymentMethodId || '');

  React.useEffect(() => setSelectedMethodId(paymentMethodId || ''), [paymentMethodId]);
  const loadInvoices = React.useCallback(async () => {
    const result = await getUserInvoices({ subjectType: type, subjectId: id });
    setInvoices(result.invoices);
  }, [id, type]);
  React.useEffect(() => { loadInvoices(); }, [loadInvoices]);
  const handleInvoicePaid = React.useCallback(async () => {
    await Promise.all([loadInvoices(), onUpdated()]);
  }, [loadInvoices, onUpdated]);

  const openInvoices = invoices?.filter((invoice) => invoice.status === 'open') ?? [];
  const outstanding = openInvoices.reduce((total, invoice) => total + (invoice.amountRemaining ?? invoice.amountDue ?? 0), 0);

  const saveMethod = async (newPaymentMethodId: string) => {
    const toastId = toast.loading('Saving payment method…');
    try {
      if (type === 'campaign') await updateCampaign(id, { paymentMethodId: newPaymentMethodId });
      else await updateAdCampaign(id, { paymentMethodId: newPaymentMethodId });
      await onUpdated();
      toast.success('Payment method saved.', { id: toastId });
    } catch (error: any) {
      setSelectedMethodId(paymentMethodId || '');
      toast.error(error.response?.data?.error || 'Unable to save the payment method.', { id: toastId });
    }
  };

  const onPaymentMethodChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newPaymentMethodId = event.target.value as string;
    if (!newPaymentMethodId || newPaymentMethodId === selectedMethodId) return;
    setSelectedMethodId(newPaymentMethodId);
    saveMethod(newPaymentMethodId);
  };

  return (
    <div className={classes.root}>
      <Card variant="outlined" className={classes.card}>
        <BillingCardHeader
          icon={<AccountBalanceWalletRounded />}
          title="Billing Summary"
          description="Upcoming charges, billing cadence, and payment details"
        />
        <CardContent className={classes.cardBody}>
          <div className={classes.billingLayout}>
            <div>
              <div className={classes.summaryGrid}>
                <div className={classes.summaryItem}>
                  <div className={classes.label}>Next billing date</div>
                  <div className={classes.value}>{nextBillingDate ? new Date(nextBillingDate).toLocaleDateString() : 'Not scheduled'}</div>
                </div>
                {!summaryContent ? (
                  <div className={classes.summaryItem}>
                    <div className={classes.label}>Billing</div>
                    <div className={classes.value}>{amount != null ? `${formatCents(amount)} ${cadence.toLowerCase()}` : cadence}</div>
                  </div>
                ) : (<div className={classes.summaryContent}>{summaryContent}</div>)}
              </div>
              {!!openInvoices.length && (
                <div className={classes.overdue}>
                  <WarningRounded />
                  <span>
                    <b>
                      {formatCents(outstanding)}
                      {' '}
                      overdue
                    </b>
                    {' '}
                    across
                    {' '}
                    {openInvoices.length}
                    {' '}
                    invoice
                    {openInvoices.length === 1 ? '' : 's'}
                    .
                  </span>
                </div>
              )}
            </div>
            <div className={classes.paymentPanel}>
              <Typography component="h3" className={classes.paymentTitle}>
                <CreditCardRounded aria-hidden="true" />
                Payment Method
              </Typography>
              <div className={classes.methodControl}>
                <PaymentMethodSelector
                  labelText=""
                  inputProps={{
                    value: selectedMethodId,
                    disabled: !editable,
                    onChange: onPaymentMethodChange,
                  }}
                />
              </div>
              <Typography variant="body2" color="textSecondary" className={classes.paymentHelper}>
                Changes apply to future charges and do not automatically pay overdue invoices.
              </Typography>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card variant="outlined" className={classes.card}>
        <BillingCardHeader
          icon={<ReceiptRounded />}
          title="Invoices"
          description="Payment history and outstanding charges for this product only"
          action={(
            <CustomButton className={classes.billingButton} color="primary" round onClick={() => history.push('/dashboard/billing?tab=invoices')}>
              View all billing
            </CustomButton>
          )}
        />
        <CardContent className={classes.cardBody}>
          <InvoicesTable invoices={invoices} allowRetry onRetrySuccess={handleInvoicePaid} />
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductBillingTab;
